import React, { useState, useMemo } from "react";
import Bg from "../../assets/images/Lp/bg-calc.jpg";
import "./Calculator.css";

export default function CustomerGeniusROICalculator({ id }) {
  const [inputs, setInputs] = useState({
    monthlyOrderVolume: "4500",
    aov: "40",
    grossMargin: "35",
    averageDiscount: "30",
    fraudulentOrderPct: "1",
    returnRate: "38",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const results = useMemo(() => {
    const {
      monthlyOrderVolume,
      aov,
      grossMargin,
      averageDiscount,
      fraudulentOrderPct,
      returnRate,
    } = {
      monthlyOrderVolume: parseFloat(inputs.monthlyOrderVolume) || 0,
      aov: parseFloat(inputs.aov) || 0,
      grossMargin: parseFloat(inputs.grossMargin) || 0,
      averageDiscount: parseFloat(inputs.averageDiscount) || 0,
      fraudulentOrderPct: parseFloat(inputs.fraudulentOrderPct) || 0,
      returnRate: parseFloat(inputs.returnRate) || 0,
    };

    const customerGeniusCost =
      monthlyOrderVolume <= 2500
        ? 9.99
        : monthlyOrderVolume <= 5000
        ? 19.99
        : 29.99;

    const gm = grossMargin / 100;
    const disc = averageDiscount / 100;
    const fraudPct = fraudulentOrderPct / 100;
    const retRate = returnRate / 100;

    const monthlyFraudulentOrders = monthlyOrderVolume * fraudPct;
    const rawReturnOrders = monthlyFraudulentOrders * retRate;
    const returnOrders = Math.round(rawReturnOrders);

    const profitLostRefunded = -monthlyFraudulentOrders * aov * (gm - disc);
    const profitGainedReturningCheaters = returnOrders * aov * gm;
    const totalMargin = profitLostRefunded + profitGainedReturningCheaters;
    const roi = customerGeniusCost ? totalMargin / customerGeniusCost : 0;

    const currency = (num) =>
      num.toLocaleString(undefined, { style: "currency", currency: "USD" });

    return {
      monthlyFraudulentOrders,
      returnOrders,
      profitLostRefunded,
      profitGainedReturningCheaters,
      totalMargin,
      roi,
      customerGeniusCost,
      currency,
    };
  }, [inputs]);

  const InputField = ({ label, name, step, suffix }) => (
    <div className="flex flex-col gap-1">
      <label htmlFor={name} className="text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type="number"
          step={step}
          value={inputs[name]}
          onChange={handleChange}
          className="w-full border rounded px-3 py-2 pr-10 text-right"
        />
        {suffix && (
          <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 text-sm">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );

  const Result = ({ label, value, negative, classCSS }) => (
    <div className={`flex flex-col gap-1  ${classCSS}`}>
      <span className="text-sm text-gray-500">{label}</span>
      <span
        className={`text-lg font-medium ${
          negative ? "text-red-600" : "text-emerald-600"
        }`}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div id={id} className="calculator-area section py-constant">
      <div
        className="container"
        style={{
          backgroundImage: `url(${Bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <h2 className="title h2 font-bold">
          Wait! Want to See How Much You Could Save
        </h2>

        <div className="calculator-inner">
          <div className="dt-container">
            <h3>Assumptions</h3>
            <span className="text-sm">
              Default results are set according to our data set from real
              customers
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 input-area">
            {/* Monthly Order Volume */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="monthlyOrderVolume"
                className="text-sm font-medium"
              >
                Monthly Order Volume
              </label>
              <div className="relative">
                <input
                  id="monthlyOrderVolume"
                  name="monthlyOrderVolume"
                  type="number"
                  value={inputs.monthlyOrderVolume}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 pr-10 text-right"
                />
              </div>
            </div>

            {/* Average Order Value */}
            <div className="flex flex-col gap-1">
              <label htmlFor="aov" className="text-sm font-medium">
                Average Order Value ($)
              </label>
              <div className="relative">
                <input
                  id="aov"
                  name="aov"
                  type="number"
                  value={inputs.aov}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 pr-10 text-right"
                />
              </div>
            </div>

            {/* Gross Margin */}
            <div className="flex flex-col gap-1">
              <label htmlFor="grossMargin" className="text-sm font-medium">
                Gross Margin
              </label>
              <div className="relative">
                <input
                  id="grossMargin"
                  name="grossMargin"
                  type="number"
                  step="0.1"
                  value={inputs.grossMargin}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 pr-10 text-right"
                />
                <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 text-sm">
                  %
                </span>
              </div>
            </div>

            {/* Average Discount */}
            <div className="flex flex-col gap-1">
              <label htmlFor="averageDiscount" className="text-sm font-medium">
                Average Discount
              </label>
              <div className="relative">
                <input
                  id="averageDiscount"
                  name="averageDiscount"
                  type="number"
                  step="0.1"
                  value={inputs.averageDiscount}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 pr-10 text-right"
                />
                <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 text-sm">
                  %
                </span>
              </div>
            </div>

            {/* Fraudulent Order % */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="fraudulentOrderPct"
                className="text-sm font-medium"
              >
                Fraudulent Order %
              </label>
              <div className="relative">
                <input
                  id="fraudulentOrderPct"
                  name="fraudulentOrderPct"
                  type="number"
                  step="0.01"
                  value={inputs.fraudulentOrderPct}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 pr-10 text-right"
                />
                <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 text-sm">
                  %
                </span>
              </div>
            </div>

            {/* Return Rate */}
            <div className="flex flex-col gap-1">
              <label htmlFor="returnRate" className="text-sm font-medium">
                Return % Rate
              </label>
              <div className="relative">
                <input
                  id="returnRate"
                  name="returnRate"
                  type="number"
                  step="0.01"
                  value={inputs.returnRate}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 pr-10 text-right"
                />
                <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 text-sm">
                  %
                </span>
              </div>
            </div>
          </div>

          <div className="dt-container">
            <h3>Calculated Fields</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 answer-area">
            <Result
              label="Monthly Fraudulent Orders"
              value={results.monthlyFraudulentOrders.toFixed(0)}
            />
            <Result label="Return Orders" value={results.returnOrders} />
            <Result
              label="Profit Lost from Refunded Orders"
              value={results.currency(results.profitLostRefunded)}
              negative={results.profitLostRefunded < 0}
            />
            <Result
              label="Profit Gained from Returning Cheaters"
              value={results.currency(results.profitGainedReturningCheaters)}
            />
            <Result
              label="CustomerGenius Cost"
              value={results.currency(results.customerGeniusCost)}
            />
            <Result
              classCSS="bold-container"
              label="Total Margin Gained / Lost"
              value={results.currency(results.totalMargin)}
              negative={results.totalMargin < 0}
            />
            <Result
              classCSS="bold-container"
              label="Return on Investment"
              value={results.roi.toFixed(2) + "x"}
              negative={results.roi < 1}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
